// Non-destructive sync for a per-user DOCUMENT blob — one row holding one record
// with many fields (`user_identity.data`), as opposed to syncBlob's
// id-collections.
//
// ─── The bug this replaces ──────────────────────────────────────────────────
// The identity store wrote its WHOLE local document on every change, with no
// compare-and-swap. A tab that had been open since yesterday therefore wrote
// yesterday's copy of every field back whenever it changed one — undoing what
// another tab or device had done in between. The worst case was privacy: AI
// visibility stores a refusal as `false` and an allowance as a DELETED key, so
// a stale tab flipped a source the user had just turned off back to allowed.
// Several of the stale writers are automatic (the level baseline, page-guide
// marks, journey and foundation high-water sets), so the user did not even
// have to touch the stale tab.
//
// ─── What this does instead: a three-way merge ──────────────────────────────
// The tab remembers BASE — the last cloud copy it agreed with (adopted on load
// and after each confirmed write). Every write re-reads the cloud copy and, field
// by field:
//   * untouched here (local equals base)  → the cloud's value stands;
//   * changed only here (cloud equals base) → ours goes in;
//   * changed on both sides               → the field's rule decides.
// So a tab can only ever write what it actually changed; a stale tab's copy of
// everything else never leaves it. Deletions need no tombstones: a key that is
// in base and missing locally was removed HERE, and is removed from the cloud.
// The write itself is syncBlob's (re-read, compare-and-swap on `updated_at`,
// retry, one at a time, flushed on pagehide).
//
// ⚠️ Without a base (a cache written before this shipped) the tab cannot tell
// its own edits from staleness, so it assumes it has none: the cloud wins.
// That is exactly what the old load did, so nothing regresses.
// ⚠️ With NO ROW at all, everything the tab holds counts as unsaved and is
// written — a new account's first save. (A console content reset also deletes
// the row; contentReset.ts clears every browser on its next boot, which is the
// designated answer to a tab that outlived a reset.)

import { createBlobSync, type BlobSync, type BlobTransport, type RefreshOutcome } from './syncBlob';

/**
 * How a field resolves when BOTH sides changed it since base. (When only one
 * side changed it, that side wins whatever the rule.)
 */
export type MergeRule =
  /** Atomic: this tab's value wins — it is the later write. The default. */
  | 'value'
  /** Array as a set: this tab's additions and removals apply to the cloud's. */
  | 'set'
  /** Ordered array: as `set`, but duplicates count and additions keep their index. */
  | 'list'
  /** Monotone scalar: the larger value (a high-water mark). Null/absent loses. */
  | 'max'
  /** Monotone scalar: the smaller value (a first-reached date). Null/absent loses. */
  | 'min'
  /** Object keyed by id: every key merged on its own with the inner rule. */
  | { map: MergeRule }
  /**
   * Array of records keyed by `records` (e.g. `'id'`, `'date'`): each record
   * merged on its own. A record changed on both sides goes to `of` (default:
   * this tab's copy). ⚠️ A DELETE ON EITHER SIDE BEATS AN EDIT ON THE OTHER —
   * otherwise any stale tab that touched a record (an automatic relink, say)
   * would resurrect it after another device deleted it.
   * Its base is stored as one hash per record (see `compactBase`), so a long
   * history does not double in localStorage.
   */
  | { records: string; of?: MergeRule }
  /** A nested object whose fields have rules of their own (unlisted fields: `'value'`). */
  | { fields: Record<string, MergeRule> }
  /** Anything else — receives the three versions of the field. */
  | ((base: unknown, local: unknown, cloud: unknown) => unknown);

export type DocRules<D> = Partial<Record<keyof D, MergeRule>>;

/** What a tab stores as its base: the document, with `records` fields as `{ key: hash }`. */
export type DocBase<D> = { [K in keyof D]?: unknown };

// ─── equality ───────────────────────────────────────────────────────────────

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** JSON-normalise (Dates → strings, undefined keys dropped) and sort keys. */
function canonical(v: unknown): unknown {
  const text = v === undefined ? undefined : JSON.stringify(v);
  if (text === undefined) return undefined;
  const plain = JSON.parse(text) as unknown;
  const sort = (x: unknown): unknown => {
    if (Array.isArray(x)) return x.map(sort);
    if (isPlainObject(x)) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(x).sort()) out[k] = sort(x[k]);
      return out;
    }
    return x;
  };
  return sort(plain);
}

/** A stable string for a JSON value. Postgres jsonb reorders object keys, so key order must not count. */
export function jsonKey(v: unknown): string {
  return v === undefined ? 'undefined' : JSON.stringify(canonical(v));
}

/** Structural equality as the database would see it (key order ignored). */
export function sameJson(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return jsonKey(a) === jsonKey(b);
}

// ─── the merge ──────────────────────────────────────────────────────────────

function pickMonotone(local: unknown, cloud: unknown, dir: 1 | -1): unknown {
  if (local === null || local === undefined) return cloud;
  if (cloud === null || cloud === undefined) return local;
  if (typeof local !== typeof cloud) return local;
  const localWins = dir === 1
    ? (local as number | string) >= (cloud as number | string)
    : (local as number | string) <= (cloud as number | string);
  return localWins ? local : cloud;
}

const asArray = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

function mergeSet(base: unknown, local: unknown, cloud: unknown): unknown[] {
  const baseKeys = new Set(asArray(base).map(jsonKey));
  const localKeys = new Set(asArray(local).map(jsonKey));
  const removed = new Set([...baseKeys].filter(k => !localKeys.has(k)));
  const out = asArray(cloud).filter(x => !removed.has(jsonKey(x)));
  const have = new Set(out.map(jsonKey));
  for (const x of asArray(local)) {
    const k = jsonKey(x);
    if (!baseKeys.has(k) && !have.has(k)) {
      out.push(x);
      have.add(k);
    }
  }
  return out;
}

/** Count of each value — a list can hold the same string twice. */
function tally(list: unknown[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const x of list) m.set(jsonKey(x), (m.get(jsonKey(x)) ?? 0) + 1);
  return m;
}

function mergeList(base: unknown, local: unknown, cloud: unknown): unknown[] {
  const baseCount = tally(asArray(base));
  const localCount = tally(asArray(local));
  // Removed here: how many fewer of each value this tab holds than base did.
  const toRemove = new Map<string, number>();
  for (const [k, n] of baseCount) {
    const fewer = n - (localCount.get(k) ?? 0);
    if (fewer > 0) toRemove.set(k, fewer);
  }
  const out: unknown[] = [];
  for (const x of asArray(cloud)) {
    const k = jsonKey(x);
    const left = toRemove.get(k) ?? 0;
    if (left > 0) toRemove.set(k, left - 1);
    else out.push(x);
  }
  // Added here: the copies beyond what base held, placed right after the entry
  // that precedes them in this tab — so the front of a newest-first history
  // stays the front, and an appended item lands after the one it followed.
  const seen = new Map<string, number>();
  const localList = asArray(local);
  localList.forEach((x, index) => {
    const k = jsonKey(x);
    const nth = (seen.get(k) ?? 0) + 1;
    seen.set(k, nth);
    if (nth <= (baseCount.get(k) ?? 0)) return;
    if (index === 0) {
      out.unshift(x);
      return;
    }
    const anchor = jsonKey(localList[index - 1]);
    let at = -1;
    for (let i = out.length - 1; i >= 0; i--) {
      if (jsonKey(out[i]) === anchor) { at = i; break; }
    }
    if (at === -1) out.push(x);
    else out.splice(at + 1, 0, x);
  });
  return out;
}

/** cyrb53 — a fast 53-bit string hash. Only ever compared for equality. */
function cyrb53(str: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/** A record's fingerprint — the same value however its keys are ordered. */
export const hashJson = (v: unknown): string => cyrb53(jsonKey(v));

const recordKey = (field: string) => (r: unknown): string =>
  String(isPlainObject(r) ? r[field] : r);

/** First record per key — what `.find(r => r.key === k)` would pick. */
function firstByKey(list: unknown[], keyOf: (r: unknown) => string): Map<string, unknown> {
  const m = new Map<string, unknown>();
  for (const r of list) if (!m.has(keyOf(r))) m.set(keyOf(r), r);
  return m;
}

/** A records field's base as `key → hash`, whether stored compact or given in full. */
function baseHashes(base: unknown, keyOf: (r: unknown) => string): Map<string, string> {
  if (Array.isArray(base)) {
    const m = new Map<string, string>();
    for (const [k, r] of firstByKey(base, keyOf)) m.set(k, hashJson(r));
    return m;
  }
  return new Map(Object.entries(isPlainObject(base) ? (base as Record<string, string>) : {}));
}

function mergeRecords(field: string, inner: MergeRule, base: unknown, local: unknown, cloud: unknown): unknown[] {
  const keyOf = recordKey(field);
  const L = firstByKey(asArray(local), keyOf);
  const C = firstByKey(asArray(cloud), keyOf);
  const B = baseHashes(base, keyOf);
  const fullBase = Array.isArray(base) ? firstByKey(base, keyOf) : null;
  const out = new Map<string, unknown>();
  for (const k of new Set([...L.keys(), ...C.keys()])) {
    const l = L.get(k);
    const c = C.get(k);
    const hb = B.get(k);
    const hl = l === undefined ? undefined : hashJson(l);
    const hc = c === undefined ? undefined : hashJson(c);
    if (hl === hb) { if (c !== undefined) out.set(k, c); continue; }             // untouched here
    if (hc === hb || hc === hl) { if (l !== undefined) out.set(k, l); continue; } // changed only here
    if (l === undefined || c === undefined) continue;                            // a delete beats an edit
    out.set(k, merge3(inner, fullBase?.get(k), l, c));
  }
  // This tab's order, with records only the cloud holds placed after the one
  // that precedes them there.
  const result: unknown[] = [];
  const placed = new Set<string>();
  for (const k of L.keys()) {
    if (out.has(k)) { result.push(out.get(k)); placed.add(k); }
  }
  let prev: string | null = null;
  for (const k of C.keys()) {
    if (!out.has(k)) continue;
    if (!placed.has(k)) {
      const at = prev === null ? 0 : result.findIndex(r => keyOf(r) === prev) + 1;
      result.splice(at, 0, out.get(k));
      placed.add(k);
    }
    prev = k;
  }
  return result;
}

/** The base a tab stores: `records` fields hashed per record, everything else as is. */
export function compactBase<D extends object>(rules: DocRules<D>, doc: D): DocBase<D> {
  const out: Record<string, unknown> = { ...(doc as Record<string, unknown>) };
  for (const [key, rule] of Object.entries(rules as Record<string, MergeRule>)) {
    if (typeof rule === 'object' && 'records' in rule) {
      const keyOf = recordKey(rule.records);
      out[key] = Object.fromEntries(baseHashes(out[key], keyOf));
    }
  }
  return out as DocBase<D>;
}

function mergeMap(inner: MergeRule, base: unknown, local: unknown, cloud: unknown): unknown {
  // A map replaced by a non-object (or the reverse) is a plain value change.
  if (!isPlainObject(local) || !isPlainObject(cloud)) return local;
  const b = isPlainObject(base) ? base : {};
  const out: Record<string, unknown> = {};
  const keys = new Set([...Object.keys(b), ...Object.keys(local), ...Object.keys(cloud)]);
  for (const key of keys) {
    const v = merge3(inner, b[key], local[key], cloud[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

function mergeFields(fields: Record<string, MergeRule>, base: unknown, local: unknown, cloud: unknown): unknown {
  if (!isPlainObject(local) || !isPlainObject(cloud)) return local;
  const b = isPlainObject(base) ? base : {};
  const out: Record<string, unknown> = {};
  for (const key of new Set([...Object.keys(cloud), ...Object.keys(local)])) {
    const v = merge3(fields[key] ?? 'value', b[key], local[key], cloud[key]);
    if (v !== undefined) out[key] = v;
  }
  return out;
}

/** Merge one field: this tab's edits since `base`, applied to the cloud's copy. */
export function merge3(rule: MergeRule, base: unknown, local: unknown, cloud: unknown): unknown {
  // Untouched here — whatever the cloud holds now is the truth, even if it is
  // "less" than ours (a content reset, another device's delete).
  if (sameJson(local, base)) return cloud;
  // Changed only here, or both sides made the same change.
  if (sameJson(cloud, base) || sameJson(cloud, local)) return local;
  if (typeof rule === 'function') return rule(base, local, cloud);
  if (typeof rule === 'object' && 'records' in rule) return mergeRecords(rule.records, rule.of ?? 'value', base, local, cloud);
  if (typeof rule === 'object' && 'fields' in rule) return mergeFields(rule.fields, base, local, cloud);
  if (typeof rule === 'object') return mergeMap(rule.map, base, local, cloud);
  switch (rule) {
    case 'set': return mergeSet(base, local, cloud);
    case 'list': return mergeList(base, local, cloud);
    case 'max': return pickMonotone(local, cloud, 1);
    case 'min': return pickMonotone(local, cloud, -1);
    default: return local;
  }
}

/** Merge a whole document field by field. Fields without a rule are `'value'`. */
export function mergeDoc<D extends object>(rules: DocRules<D>, base: DocBase<D>, local: D, cloud: D): D {
  const out = {} as Record<string, unknown>;
  const b = base as Record<string, unknown>;
  const l = local as Record<string, unknown>;
  const c = cloud as Record<string, unknown>;
  for (const key of Object.keys(l)) {
    out[key] = merge3((rules as Record<string, MergeRule>)[key] ?? 'value', b[key], l[key], c[key]);
  }
  return out as D;
}

/**
 * Only the fields of `next` that really differ from `prev`. Adopting a cloud
 * copy through this keeps every unchanged field's object identity, so the
 * adoption re-renders what moved rather than every identity reader.
 */
export function changedFields<D extends object>(prev: Partial<D>, next: D): Partial<D> {
  const out: Partial<D> = {};
  for (const key of Object.keys(next) as (keyof D)[]) {
    if (!sameJson(prev[key], next[key])) out[key] = next[key];
  }
  return out;
}

// ─── the sync ───────────────────────────────────────────────────────────────

export interface DocSyncOptions<D extends object> {
  transport: BlobTransport;
  /** The account this tab's document and base belong to — see BlobSyncOptions.owner. */
  owner: () => string | null;
  rules: DocRules<D>;
  /** Raw cloud data (null = no row) → the full document, defaults filled in. */
  hydrate: (data: unknown) => D;
  /** This tab's current document. */
  read: () => D;
  /** The last cloud document this tab agreed with (as stored — see `compactBase`); null = unknown. */
  readBase: () => DocBase<D> | null;
  /** Replace this tab's document AND its base (one `set`). The base arrives compacted. */
  adopt: (doc: D, base: DocBase<D>) => void;
  /** After a confirmed write — `previousBase` is the base before it; `user` = the account written for. */
  onWritten?: (doc: D, previousBase: DocBase<D> | null, user: string | undefined) => void;
  onError?: (error: unknown) => void;
  debounceMs?: number;
  maxAttempts?: number;
}

export interface DocSync<D extends object> extends Omit<BlobSync, 'refresh' | 'writeBack'> {
  /**
   * Fold a freshly LOADED cloud copy (null = no row) into this tab: keep the
   * edits this tab made since its base, take the cloud for everything else,
   * and adopt the cloud copy as the new base. Returns true when this tab still
   * holds edits the cloud lacks (the caller then schedules a write).
   */
  reconcile: (cloudData: unknown) => boolean;
  /**
   * A stale tab catches up (lib/blobFocusRefresh.ts): when the row moved since
   * this tab last held it, re-read it and `reconcile` — unsaved edits here are
   * kept (and written), everything else becomes the cloud's. `ready` = loaded,
   * for the account signed in now. See BlobSync.refresh.
   */
  refresh: (ready?: () => boolean | Promise<boolean>) => Promise<RefreshOutcome>;
  /** The base to store for `doc` — for a store recording its first edit's base. */
  baseOf: (doc: D) => DocBase<D>;
  /** Take a loaded cloud copy wholesale (a cache that belonged to another account). */
  replace: (cloudData: unknown) => void;
  /** True while this sync is writing an adopted copy into the store (a subscription skips it). */
  readonly adopting: boolean;
}

export function createDocSync<D extends object>(options: DocSyncOptions<D>): DocSync<D> {
  const { transport, owner, rules, hydrate, read, readBase, onWritten, onError, debounceMs, maxAttempts } = options;
  let adopting = false;
  const adopt = (doc: D, base: D | DocBase<D>) => {
    adopting = true;
    try {
      options.adopt(doc, compactBase(rules, base as D));
    } finally {
      adopting = false;
    }
  };

  // What the in-flight write was built from, so a confirmed write can keep any
  // edits made while it was on the wire.
  let built: { from: D; doc: D } | null = null;

  /** What this tab's edits are measured against — see the header. */
  const baseFor = (cloudData: unknown, local: D): DocBase<D> => {
    if (cloudData === null || cloudData === undefined) return hydrate(null); // no row: all of it is unsaved
    return readBase() ?? local; // no base: nothing here can be proven an edit
  };

  const blob = createBlobSync({
    transport,
    owner,
    onError,
    debounceMs,
    maxAttempts,
    build: (cloudData) => {
      const cloud = hydrate(cloudData);
      const local = read();
      const base = baseFor(cloudData, local);
      const doc = mergeDoc(rules, base, local, cloud);
      if (sameJson(doc, cloud)) {
        // Nothing of ours is missing from the cloud. Adopt its copy now — that is
        // what keeps the base honest, and it refreshes a stale tab for free.
        if (!sameJson(local, cloud) || !sameJson(readBase(), compactBase(rules, cloud))) adopt(cloud, cloud);
        built = null;
        return null;
      }
      built = { from: local, doc };
      // Keys this build does not know (a newer release's fields, retired ones)
      // ride along untouched instead of being dropped.
      return { ...(isPlainObject(cloudData) ? cloudData : {}), ...doc };
    },
    onWritten: (_payload, user) => {
      if (!built) return;
      const { from, doc } = built;
      built = null;
      const previousBase = readBase();
      // Edits made while the write was in flight survive; everything else
      // becomes exactly what the cloud now holds.
      adopt(mergeDoc(rules, from, read(), doc), doc);
      onWritten?.(doc, previousBase, user);
    },
  });

  const reconcile = (cloudData: unknown): boolean => {
    const cloud = hydrate(cloudData);
    const local = read();
    const doc = mergeDoc(rules, baseFor(cloudData, local), local, cloud);
    adopt(doc, cloud);
    return !sameJson(doc, cloud);
  };

  return {
    ...blob,
    get adopting() { return adopting; },
    baseOf: (doc) => compactBase(rules, doc),
    replace(cloudData) {
      const cloud = hydrate(cloudData);
      adopt(cloud, cloud);
    },
    reconcile,
    refresh: (ready) => blob.refresh(reconcile, ready),
  };
}
